/**
 * Codex PR #784 round-11 P2 (`viewports/viewportLiftRequests.ts:219`) — the
 * POINT-PROXY feed for ids only a paper-space VIEWPORT renders.
 *
 * # The gap this closes
 *
 * Round 10 stopped a viewport requesting a LIFT for what it renders in the
 * 0.5-2 px "point" band, because merging those ids into `nextVisibleSet`
 * resolved every one-pixel reference into its full wrapper + satellite tree.
 * The doc justified the exclusion with "the `point`-band ids keep their point
 * proxy" — which holds only while the MAIN camera also puts them in that band.
 * `scene/FrameLoops.tsx` builds the main points cloud from the main camera's
 * `groups.point`, so an id below 0.5 px there (or outside the layout camera's
 * rect entirely, which is the normal case for model geometry seen through a
 * layout) had neither lifted geometry nor a dot: it simply disappeared from the
 * viewport that showed it.
 *
 * # The shape of the fix
 *
 * `ViewportLiftRequests.points` names those ids; this module turns them into
 * cloud entries and NOTHING else. Each is resolved through the NON-EXPANDING
 * proxy (`EntityLiftRequest.proxyOnly` — the metadata stub: real bounds, real
 * resolved colour, `geometry.kind: "point"`), so a viewport holding 20 000
 * one-pixel references costs 20 000 dots and zero wrappers, satellites, bucket
 * slots or direct meshes. That is exactly the trade the main camera makes for
 * its own point band, and it is why the round-10 blow-up cannot come back
 * through this path.
 *
 * # Round-12 P2 — the dots are ATTRIBUTED, not a union
 *
 * Codex PR #784 round-12 P2 (`scene/FrameLoops.tsx:1097`) — round 11 fed the
 * proxies into the SAME cloud as the main camera's own point band. That cloud
 * carries only `kind: "lod-points-cloud"`, and every paper-space mask is keyed
 * on `entityId` / `keyId` / `spaceId`, so the union of every viewport's ids was
 * drawn unmasked in EVERY viewport pass and on the bare sheet.
 *
 * The proxies are therefore returned GROUPED, one group per
 * (requesting-viewport set, layer) — the two attributions a mask decision needs
 * — and `FrameLoops` builds one small cloud per group, stamped through
 * `rendering/viewportPointProxyStamp.ts`. Grouping by the requester SET (not
 * per viewport) is what keeps the feed non-expanding: an id K viewports show in
 * the point band is still exactly ONE dot, in the cloud whose membership is
 * those K viewports. The number of clouds is the number of distinct
 * (membership, layer) pairs actually present — at most one per dot, in practice
 * a handful — and the number of DOTS is invariant at one per requested id.
 *
 * The main camera's own cloud is built separately and untouched: it keeps its
 * unstamped userData and its pre-round-11 behaviour, which is also why an id
 * the main camera ALREADY dots is skipped here rather than dotted twice.
 */

import type { RenderableEntity, SpatialIndex } from "../types";
import type { EntityLiftRequest } from "./SceneRefsTypes";
import type { ViewportLiftRequests } from "../viewports/viewportLiftRequests";

/** The single `proxyOnly` request object — constant, so it never allocates. */
const PROXY_REQUEST: EntityLiftRequest = { proxyOnly: true };

/** The empty answer — shared, so the common frame allocates nothing. */
const NO_GROUPS: readonly ViewportPointProxyGroup[] = [];

/**
 * One cloud's worth of proxies: the dots that share BOTH a requesting-viewport
 * set and a layer, so a single object-level mask decision
 * (`rendering/viewportRenderPass.ts applySceneVisibilityMask`) is exact for
 * every one of them.
 */
export interface ViewportPointProxyGroup {
  /** Ids of the viewports that asked for these dots. Never empty. */
  readonly viewportIds: readonly string[];
  /** The layer every entity in {@link entities} sits on. */
  readonly layer: string;
  /** The proxies themselves — one per requested id. */
  readonly entities: RenderableEntity[];
}

/**
 * Resolve one proxy per viewport-only point-band id and group them for the
 * cloud builder. Returns an EMPTY array when there is nothing to draw — model
 * space, no viewports, or every requested id already drawn — so the common
 * frame pays one `Map`-size check.
 *
 * An id is skipped when it is already LIFTED (the renderer draws its real
 * geometry this frame) or already in the main camera's point bucket (it would
 * be a second dot at the same place, and the main cloud is the one that owns
 * it).
 *
 * Resolution order is `spatial.resolve` first — it answers with the REAL
 * renderable for an id that has been lifted at some point — then the proxy
 * resolve, which is the only one that can answer for an id the main-camera
 * sub-pixel floor withholds. An id neither can answer for (unknown to the
 * metadata index, or a legacy pass with no resolver wired) contributes nothing.
 *
 * A proxy that does not resolve to MODEL space contributes nothing either: a
 * paper-space viewport is a window onto model space, and
 * `viewportCullingAdapter.query` keeps an unresolvable id conservatively — so
 * a paper-space id can reach the request set, and drawing it would put layout
 * geometry inside a viewport window.
 */
export function collectViewportPointProxies(args: {
  requests: ViewportLiftRequests | undefined;
  spatial: SpatialIndex;
  resolver:
    | ((id: string, request?: EntityLiftRequest) => RenderableEntity | null)
    | null;
  lifted: ReadonlySet<string>;
  mainPoints: ReadonlyArray<RenderableEntity>;
}): readonly ViewportPointProxyGroup[] {
  const requested = args.requests?.points;
  if (!requested || requested.size === 0) return NO_GROUPS;
  let drawn: Set<string> | null = null;
  let groups: Map<string, ViewportPointProxyGroup> | null = null;
  for (const [id, viewportIds] of requested) {
    if (viewportIds.length === 0) continue;
    if (args.lifted.has(id)) continue;
    if (drawn === null) {
      drawn = new Set<string>();
      for (const entity of args.mainPoints) drawn.add(entity.id);
    }
    if (drawn.has(id)) continue;
    const proxy =
      args.spatial.resolve(id) ?? args.resolver?.(id, PROXY_REQUEST) ?? null;
    if (!proxy) continue;
    if (proxy.spaceId !== "model") continue;
    // NUL / SOH separators: neither can occur inside a DXF layer name or an
    // entity id, so the key is injective over (layer, membership).
    const key = `${proxy.layer}\u0000${viewportIds.join("\u0001")}`;
    if (groups === null) groups = new Map<string, ViewportPointProxyGroup>();
    const group = groups.get(key);
    if (group) {
      group.entities.push(proxy);
    } else {
      groups.set(key, {
        viewportIds,
        layer: proxy.layer,
        entities: [proxy],
      });
    }
    drawn.add(id);
  }
  return groups === null ? NO_GROUPS : [...groups.values()];
}
